import React, { useState, useEffect } from "react";
import { apiPost } from "../api";
import { SvgIcon } from "./SvgIcon";
import { BtnSpinner } from "./BtnSpinner";
import { t, Locale } from "../i18n";

export function ConfirmPasswordModal(props: {
  isOpen: boolean;
  locale: Locale;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: (password: string) => void;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.isOpen) {
      setPassword("");
      setShowPassword(false);
      setVerifying(false);
      setError(null);
    }
  }, [props.isOpen]);

  async function handleSubmit() {
    if (!password) {
      setError(t(props.locale, "confirmPassword.error.required"));
      return;
    }

    setVerifying(true);
    setError(null);

    const res = await apiPost<{ ok: boolean }>("/v1/session/verify-password", { password });

    if (!res.ok) {
      setVerifying(false);
      setError(t(props.locale, "confirmPassword.error.incorrect"));
      return;
    }

    // Password verified — hand off to caller
    props.onConfirm(password);
    props.onClose();
  }

  if (!props.isOpen) return null;

  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true">
      <div className="modalCard" style={{ width: "min(420px, 92vw)" }}>
        <div className="modalHeader">
          <div className="modalTitle">{props.title}</div>
          <button className="iconBtn" onClick={props.onClose} title="Close" disabled={verifying}>
            <SvgIcon name="x-circle" title="Close" />
          </button>
        </div>

        <div className="modalBody" style={{ padding: "16px 20px" }}>
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <span style={{ display: "inline-block", width: 32, height: 32, color: "var(--muted)" }}>
              <SvgIcon name="shield-lock" title="Security verification" />
            </span>
          </div>

          <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px", lineHeight: "1.5", textAlign: "center" }}>
            {props.description}
          </div>

          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
            {t(props.locale, "confirmPassword.currentPassword")}
          </label>
          <div style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              placeholder={t(props.locale, "confirmPassword.placeholder")}
              disabled={verifying}
              autoFocus
              style={{ width: "100%", boxSizing: "border-box", paddingRight: "36px" }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={verifying}
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                color: "var(--muted)",
                display: "inline-flex",
                alignItems: "center",
                width: 20,
                height: 20
              }}
              title={showPassword ? "Hide password" : "Show password"}
            >
              <SvgIcon name={showPassword ? "eye-fill" : "eye"} title={showPassword ? "Hide" : "Show"} />
            </button>
          </div>

          {error && (
            <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "10px" }}>
              {error}
            </div>
          )}
        </div>

        <div className="modalFooter" style={{ padding: "12px 20px" }}>
          <button className="btn" onClick={props.onClose} disabled={verifying}>
            {t(props.locale, "common.cancel")}
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn primary"
            onClick={handleSubmit}
            disabled={verifying || !password}
          >
            <BtnSpinner loading={verifying}>{verifying ? t(props.locale, "confirmPassword.verifying") : props.confirmLabel}</BtnSpinner>
          </button>
        </div>
      </div>
    </div>
  );
}
